
import React, { useState, useEffect, useRef } from 'react';
import educationalListings from '../data/educational';

const placeholderImg = '/defaultimages/imagenotavailable.jpg';

const Educational = () => {
  // State management
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState('');
  const [locationname, setLocationname] = useState('');
  const [typecategory, setTypecategory] = useState('');
  const [areaname, setAreaname] = useState('');
  const [modalItem, setModalItem] = useState(null);
  const [modalPhoto, setModalPhoto] = useState(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [showToast, setShowToast] = useState(false);

  // Ref for horizontally scrolling featured row
  const featuredScrollRef = useRef(null);

  // Initialize listings from local data, filter by AccountStatus true and sort by listorder
  useEffect(() => {
    const filtered = educationalListings
      .filter(i => i.AccountStatus === true || i.accountstatus === true)
      .sort((a, b) => (a.listorder || 999) - (b.listorder || 999));
    setListings(filtered);
    setLoading(false);
  }, []);

  // When modal opens, set modalPhoto to first photo
  useEffect(() => {
    if (modalItem) {
      const photos = modalItem.photos && Array.isArray(modalItem.photos) ? modalItem.photos : [];
      setModalPhoto(photos.length > 0 ? photos[0] : modalItem.imagepath || placeholderImg);
    } else {
      setModalPhoto(null);
    }
  }, [modalItem]);

  // Keyboard and arrow navigation for modal photos
  useEffect(() => {
    if (!modalItem) return;
    const photos = modalItem.photos && Array.isArray(modalItem.photos) ? modalItem.photos : [];
    if (photos.length === 0) return;

    const handler = (e) => {
      if (e.key === 'ArrowLeft') {
        const currentIndex = photos.findIndex(p => p === modalPhoto);
        const newIndex = currentIndex > 0 ? currentIndex - 1 : photos.length - 1;
        setModalPhoto(photos[newIndex]);
      } else if (e.key === 'ArrowRight') {
        const currentIndex = photos.findIndex(p => p === modalPhoto);
        const newIndex = currentIndex < photos.length - 1 ? currentIndex + 1 : 0;
        setModalPhoto(photos[newIndex]);
      } else if (e.key === 'Escape') {
        setModalItem(null);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [modalItem, modalPhoto]);

  const scrollFeaturedBy = (delta) => {
    if (featuredScrollRef.current) {
      featuredScrollRef.current.scrollBy({ left: delta, behavior: 'smooth' });
    }
  };

  const handleViewDetails = (item) => {
    setModalItem(item);
  };

  const closeModal = () => {
    setModalItem(null);
    setShowShareMenu(false);
  };

  // Extract filter options
  const locationOptions = Array.from(new Set(listings.map(i => i.locationname || '').filter(Boolean))).sort();
  const areaOptions = locationname
    ? Array.from(new Set(listings.filter(i => i.locationname === locationname).map(i => i.areaname || '').filter(Boolean))).sort()
    : Array.from(new Set(listings.map(i => i.areaname || '').filter(Boolean))).sort();
  const typeOptions = Array.from(new Set(listings.map(i => i.typecategory || '').filter(Boolean))).sort();

  // Get name suggestions
  const suggestions = search.length > 0
    ? Array.from(new Set(
        listings
          .filter(i => i.institutename && i.institutename.toLowerCase().includes(search.toLowerCase()))
          .map(i => i.institutename)
      )).slice(0, 8)
    : [];

  // Filter listings
  let filteredListings = listings;
  if (locationname !== '') {
    filteredListings = filteredListings.filter(i => i.locationname === locationname);
  }
  if (areaname !== '') {
    filteredListings = filteredListings.filter(i => i.areaname === areaname);
  }
  if (typecategory !== '') {
    filteredListings = filteredListings.filter(i => i.typecategory === typecategory);
  }
  if (search.trim() !== '') {
    filteredListings = filteredListings.filter(i =>
      (i.institutename && i.institutename.toLowerCase().includes(search.trim().toLowerCase())) ||
      (i.typecategory && i.typecategory.toLowerCase().includes(search.trim().toLowerCase())) ||
      (i.aboutdescription && i.aboutdescription.toLowerCase().includes(search.trim().toLowerCase()))
    );
  }

  // Featured listings sorted by listorder
  const featuredListings = listings
    .filter(i => i.featuredlisting)
    .sort((a, b) => (a.listorder || 999) - (b.listorder || 999));

  // Helper to copy link (robust for mobile): returns a Promise
  const copyToClipboard = async (text) => {
    try {
      if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
        await navigator.clipboard.writeText(text);
      } else {
        // Fallback for older mobile browsers
        const textarea = document.createElement('textarea');
        textarea.value = text;
        // Prevent scrolling to bottom
        textarea.style.position = 'fixed';
        textarea.style.left = '-9999px';
        textarea.style.top = '0';
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        const successful = document.execCommand('copy');
        document.body.removeChild(textarea);
        if (!successful) {
          // Last-resort fallback: show prompt so user can long-press copy on mobile
          // eslint-disable-next-line no-alert
          window.prompt('Copy the link below', text);
        }
      }

      setShowToast(true);
      setTimeout(() => setShowToast(false), 2000);
      return true;
    } catch (err) {
      // If all else fails, prompt the user with the link (mobile-friendly)
      try {
        // eslint-disable-next-line no-alert
        window.prompt('Copy the link below', text);
      } catch (e) {
        // ignore
      }
      return false;
    }
  };

  if (loading) {
    return (
      <div style={{ width: '100%', maxWidth: '100vw', margin: 0, padding: 40, textAlign: 'center' }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>⏳</div>
        <div style={{ fontSize: 18, color: '#666' }}>Loading educational institutes...</div>
      </div>
    );
  }



  return (
    <div style={{ width: '100%', maxWidth: '100vw', margin: 0, padding: 10, boxSizing: 'border-box', overflowX: 'hidden' }}>
      {/* Header */}
      <header style={{ background: '#e8f5e9', color: '#388e3c', padding: '0.2rem', textAlign: 'center', borderRadius: 8, marginBottom: 0, marginTop:0 }}>
        <h1 style={{ margin: 0, marginTop: 0, marginBottom: 0, fontSize: '1.0rem', fontWeight: 700 }}>Educational Institutes</h1>
      </header>

      {/* Featured Section */}
      {featuredListings.length > 0 && (
        <div style={{ marginBottom: '0.2rem', position: 'relative' }}>
          {/* Left scroll button */}
          <button
            aria-label="Scroll featured left"
            onClick={() => scrollFeaturedBy(-250)}
            style={{
              position: 'absolute',
              top: '35%',
              transform: 'translateY(-50%)',
              left: 0,
              zIndex: 2,
              background: 'rgba(56, 142, 60, 0.9)',
              color: '#fff',
              border: 'none',
              borderRadius: 20,
              width: 28,
              height: 28,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 18
            }}
          >
            ‹
          </button>

          {/* Featured scroll container */}
          <div
            ref={featuredScrollRef}
            style={{
              display: 'flex',
              gap: 0,
              overflowX: 'auto',
              scrollBehavior: 'smooth',
              padding: '4px 44px',
              scrollSnapType: 'x mandatory',
              msOverflowStyle: 'none',
              scrollbarWidth: 'none'
            }}
          >
            {featuredListings.map((item) => (
              <div
                key={item.id}
                onClick={() => handleViewDetails(item)}
                style={{
                  minWidth: 120,
                  maxWidth: 135,
                  height: 180,
                  background: '#fff',
                  border: '0.25px solid #388e3c',
                  borderRadius: 12,
                  padding: 8,
                  cursor: 'pointer',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  scrollSnapAlign: 'start',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  display: 'flex',
                  flexDirection: 'column'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                }}
              >
                <img
                  src={(item.photos && item.photos[0]) || placeholderImg}
                  alt={item.institutename}
                  style={{ width: '100%', height: 70, objectFit: 'cover', borderRadius: 8, marginBottom: 0 }}
                  onError={(e) => { e.target.onerror = null; e.target.src = placeholderImg; }}
                />
                <h3 style={{ margin: 0, fontSize: 13, color: '#388e3c' }}>{item.institutename}</h3>
                <div style={{ fontSize: 12, color: '#888', marginBottom: 6, fontWeight: 600 }}>
                  📍 {item.locationname} • {item.areaname}
                </div>
                {item.contactperson && (
                  <div style={{ fontSize: 12, color: '#777', marginBottom: 1 }}>
                    Contact: <span style={{ fontWeight: 600, color: '#555' }}>{item.contactperson}</span>
                  </div>
                )}
                
                {/* Social media icons */}
                {(item.sm_instagram || item.sm_facebook || item.sm_youtube) && (
                  <div style={{ display: 'flex', gap: 6, marginTop: 2 }}>
                    {item.sm_instagram && (
                      <a
                        href={`https://instagram.com/${item.sm_instagram}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        style={{ 
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 20,
                          height: 20,
                          borderRadius: 4,
                          background: 'linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)',
                          color: '#fff',
                          fontSize: 12,
                          fontWeight: 'bold',
                          textDecoration: 'none',
                          fontFamily: 'Arial, sans-serif'
                        }}
                        title="Instagram"
                      >
                        IG
                      </a>
                    )}
                    {item.sm_facebook && (
                      <a
                        href={`https://facebook.com/${item.sm_facebook}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        style={{ 
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 20,
                          height: 20,
                          borderRadius: 4,
                          background: '#1877F2',
                          color: '#fff',
                          fontSize: 12,
                          fontWeight: 'bold',
                          textDecoration: 'none',
                          fontFamily: 'Arial, sans-serif'
                        }}
                        title="Facebook"
                      >
                        f
                      </a>
                    )}
                    {item.sm_youtube && (
                      <a
                        href={`https://youtube.com/${item.sm_youtube}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        style={{ 
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 20,
                          height: 20,
                          borderRadius: 4,
                          background: '#FF0000',
                          color: '#fff',
                          fontSize: 12,
                          fontWeight: 'bold',
                          textDecoration: 'none',
                          fontFamily: 'Arial, sans-serif'
                        }}
                        title="YouTube"
                      >
                        YT
                      </a>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Right scroll button */}
          <button
            aria-label="Scroll featured right"
            onClick={() => scrollFeaturedBy(250)}
            style={{
              position: 'absolute',
              top: '35%',
              transform: 'translateY(-50%)',
              right: 0,
              zIndex: 2,
              background: 'rgba(56, 142, 60, 0.9)',
              color: '#fff',
              border: 'none',
              borderRadius: 20,
              width: 28,
              height: 28,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 18
            }}
          >
            ›
          </button>
        </div>
      )}

      {/* Filters Section */}
      <div style={{ background: '#f5f5f5', padding: '8px 12px 8px 12px', borderRadius: 8, marginBottom: 10 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginTop: '-8px' }}>
          {/* Search with suggestions */}
          <div style={{ flex: '1 1 80px', minWidth: 120, maxWidth: 180, position: 'relative' }}>
            <label style={{ display: 'block', marginBottom: 4, fontSize: 13, fontWeight: 600, color: '#333' }}>
              Search
            </label>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onFocus={() => setShowSuggestions(true)}
              onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
              placeholder="Institute name, type, or description..."
              style={{
                width: '100%',
                padding: '4px 8px',
                fontSize: 12,
                border: '1px solid #c5e1a5',
                borderRadius: 6,
                outline: 'none',
                boxSizing: 'border-box',
                height: 28
              }}
            />
            {showSuggestions && suggestions.length > 0 && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                background: '#fff',
                border: '1px solid #c5e1a5',
                borderRadius: 6,
                marginTop: 4,
                maxHeight: 200,
                overflowY: 'auto',
                zIndex: 10,
                boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
              }}>
                {suggestions.map((suggestion, idx) => (
                  <div
                    key={idx}
                    onMouseDown={() => setSearch(suggestion)}
                    style={{
                      padding: '8px 12px',
                      cursor: 'pointer',
                      fontSize: 14,
                      borderBottom: idx < suggestions.length - 1 ? '1px solid #eee' : 'none'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#e8f5e9'}
                    onMouseLeave={(e) => e.currentTarget.style.background = '#fff'}
                  >
                    {suggestion}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Location filter */}
          <div style={{ flex: '0 1 80px', minWidth: 100, maxWidth: 140 }}>
            <label style={{ display: 'block', marginBottom: 4, fontSize: 13, fontWeight: 600, color: '#333' }}>
              Location
            </label>
            <select
              value={locationname}
              onChange={(e) => { setLocationname(e.target.value); setAreaname(''); }}
              style={{
                width: '100%',
                padding: '4px 8px',
                fontSize: 12,
                border: '1px solid #c5e1a5',
                borderRadius: 6,
                outline: 'none',
                background: '#fff',
                cursor: 'pointer',
                height: 28
              }}
            >
              <option value="">All Locations</option>
              {locationOptions.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>

          {/* Area filter */}
          <div style={{ flex: '0 1 80px', minWidth: 100, maxWidth: 140 }}>
            <label style={{ display: 'block', marginBottom: 4, fontSize: 13, fontWeight: 600, color: '#333' }}>
              Area
            </label>
            <select
              value={areaname}
              onChange={(e) => setAreaname(e.target.value)}
              disabled={areaOptions.length === 0}
              style={{
                width: '100%',
                padding: '4px 8px',
                fontSize: 12,
                border: '1px solid #c5e1a5',
                borderRadius: 6,
                outline: 'none',
                background: areaOptions.length === 0 ? '#e0e0e0' : '#fff',
                cursor: areaOptions.length === 0 ? 'not-allowed' : 'pointer',
                height: 28
              }}
            >
              <option value="">All Areas</option>
              {areaOptions.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>

          {/* Type filter */}
          <div style={{ flex: '0 1 80px', minWidth: 100, maxWidth: 140 }}>
            <label style={{ display: 'block', marginBottom: 4, fontSize: 13, fontWeight: 600, color: '#333' }}>
              Type
            </label>
            <select
              value={typecategory}
              onChange={(e) => setTypecategory(e.target.value)}
              style={{
                width: '100%',
                padding: '4px 8px',
                fontSize: 12,
                border: '1px solid #c5e1a5',
                borderRadius: 6,
                outline: 'none',
                background: '#fff',
                cursor: 'pointer',
                height: 28
              }}
            >
              <option value="">All Types</option>
              {typeOptions.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>

          {/* Clear filters button */}
          <div style={{ flex: '0 1 60px', minWidth: 80, maxWidth: 100, paddingTop: 4 }}>
            <button
              onClick={() => {
                setSearch('');
                setLocationname('');
                setAreaname('');
                setTypecategory('');
              }}
              style={{
                padding: '4px 8px',
                fontSize: 12,
                background: '#388e3c',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                cursor: 'pointer',
                fontWeight: 600,
                width: '100%',
                height: 28
              }}
            >
              Clear All
            </button>
          </div>
        </div>
      </div>

      {/* Results count */}
      <div style={{ marginBottom: 10, fontSize: 14, color: '#666' }}>
        Showing <strong>{filteredListings.length}</strong> institute{filteredListings.length !== 1 ? 's' : ''}
      </div>

      {/* Institutes Grid */}
      <div style={{ 
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(310px, 310px))',
        gap: 6,
        marginBottom: 20,
        justifyContent: 'center'
      }}>
        {filteredListings.map((item) => (
          <div
            key={item.id}
            onClick={() => handleViewDetails(item)}
            style={{
              background: '#fff',
              border: '1px solid #e0e0e0',
              borderRadius: 12,
              padding: 12,
              cursor: 'pointer',
              transition: 'transform 0.2s, box-shadow 0.2s',
              boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
              height: '280px'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-4px)';
              e.currentTarget.style.boxShadow = '0 6px 12px rgba(0,0,0,0.12)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.08)';
            }}
          >
            <img
              src={(item.photos && item.photos[0]) || placeholderImg}
              alt={item.institutename}
              style={{ width: '90%', maxWidth: 250, height: 180, objectFit: 'cover', borderRadius: 8, alignSelf: 'center' }}
              onError={(e) => { e.target.onerror = null; e.target.src = placeholderImg; }}
            />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 2 }}>
                <h3 style={{ margin: 0, fontSize: 16, color: '#388e3c' }}>{item.institutename}</h3>
                {item.featuredlisting && (
                  <span style={{ 
                    background: '#388e3c',
                    color: '#fff',
                    padding: '2px 6px',
                    borderRadius: 3,
                    fontSize: 10,
                    fontWeight: 600,
                    marginLeft: 8,
                    flexShrink: 0
                  }}>
                    Featured
                  </span>
                )}
              </div>
              {item.typecategory && <div style={{ fontSize: 13, color: '#666', marginBottom: 4, fontWeight: 500 }}>{item.typecategory}</div>}
               <div style={{ fontSize: 12, color: '#888', marginBottom: 8, fontWeight: 600 }}>
                📍 {item.locationname} • {item.areaname}
              </div>
              {item.contactperson && (
                <div style={{ fontSize: 13, color: '#444', marginBottom: 4 }}>
                  Contact: <span style={{ fontWeight: 600 }}>{item.contactperson}</span>
                </div>
              )}
             
              {/* Social media icons */}
              {(item.sm_instagram || item.sm_facebook || item.sm_youtube) && (
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  {item.sm_instagram && (
                    <a
                      href={`https://instagram.com/${item.sm_instagram}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      style={{ 
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 22,
                        height: 22,
                        borderRadius: 5,
                        background: 'linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)',
                        color: '#fff',
                        fontSize: 12,
                        fontWeight: 'bold',
                        textDecoration: 'none',
                        fontFamily: 'Arial, sans-serif'
                      }}
                      title="Instagram"
                    >
                      IG
                    </a>
                  )}
                  {item.sm_facebook && (
                    <a
                      href={`https://facebook.com/${item.sm_facebook}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      style={{ 
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 22,
                        height: 22,
                        borderRadius: 5,
                        background: '#1877F2',
                        color: '#fff',
                        fontSize: 12,
                        fontWeight: 'bold',
                        textDecoration: 'none',
                        fontFamily: 'Arial, sans-serif'
                      }}
                      title="Facebook"
                    >
                      f
                    </a>
                  )}
                  {item.sm_youtube && (
                    <a
                      href={`https://youtube.com/${item.sm_youtube}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      style={{ 
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 22,
                        height: 22,
                        borderRadius: 5,
                        background: '#FF0000',
                        color: '#fff',
                        fontSize: 12,
                        fontWeight: 'bold',
                        textDecoration: 'none',
                        fontFamily: 'Arial, sans-serif'
                      }}
                      title="YouTube"
                    >
                      ▶
                    </a>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* No results message */}
      {filteredListings.length === 0 && (
        <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🔍</div>
          <div style={{ fontSize: 18, marginBottom: 8 }}>No institutes found</div>
          <div style={{ fontSize: 14 }}>Try adjusting your filters or search terms</div>
        </div>
      )}

      {/* Modal */}
      {modalItem && (
        <div
          onClick={closeModal}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: 16
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#fff',
              borderRadius: 16,
              maxWidth: 600,
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto',
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 8px 32px rgba(0,0,0,0.2)'
            }}
          >
            {/* Close button */}
            <button
              onClick={closeModal}
              style={{
                position: 'absolute',
                top: 12,
                right: 12,
                background: 'rgba(0,0,0,0.5)',
                color: '#fff',
                border: 'none',
                borderRadius: '50%',
                width: 36,
                height: 36,
                fontSize: 24,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10
              }}
            >
              ×
            </button>

            {/* Modal content: single column layout (images on top, details below) */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: 20, overflow: 'auto' }}>
              {/* Top section: Images */}
              <div style={{ flex: '0 0 auto', maxWidth: 720, width: '100%', margin: '0 auto' }}>
                {/* Main image with overlay navigation arrows */}
                <div style={{ position: 'relative', marginBottom: 12 }}>
                  <img
                    src={modalPhoto || placeholderImg}
                    alt={modalItem.institutename}
                    style={{
                      width: '100%',
                      height: 400,
                      objectFit: 'cover',
                      borderRadius: 12
                    }}
                    onError={(e) => { e.target.onerror = null; e.target.src = placeholderImg; }}
                  />
                  
                  {/* Overlay arrow buttons */}
                  {modalItem.photos && modalItem.photos.length > 1 && (
                    <>
                      <button
                        onClick={() => {
                          const idx = modalItem.photos.findIndex(p => p === modalPhoto);
                          const newIdx = idx > 0 ? idx - 1 : modalItem.photos.length - 1;
                          setModalPhoto(modalItem.photos[newIdx]);
                        }}
                        style={{
                          position: 'absolute',
                          left: 8,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          background: 'rgba(0,0,0,0.5)',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '50%',
                          width: 32,
                          height: 32,
                          cursor: 'pointer',
                          fontSize: 18,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          zIndex: 5
                        }}
                      >
                        ‹
                      </button>
                      <button
                        onClick={() => {
                          const idx = modalItem.photos.findIndex(p => p === modalPhoto);
                          const newIdx = idx < modalItem.photos.length - 1 ? idx + 1 : 0;
                          setModalPhoto(modalItem.photos[newIdx]);
                        }}
                        style={{
                          position: 'absolute',
                          right: 8,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          background: 'rgba(0,0,0,0.5)',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '50%',
                          width: 32,
                          height: 32,
                          cursor: 'pointer',
                          fontSize: 18,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          zIndex: 5
                        }}
                      >
                        ›
                      </button>
                    </>
                  )}
                </div>

                {/* Thumbnail strip */}
                {modalItem.photos && modalItem.photos.length > 1 && (
                  <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 4, justifyContent: 'center' }}>
                    {modalItem.photos.map((photo, idx) => (
                      <img
                        key={idx}
                        src={photo}
                        alt={`Thumbnail ${idx + 1}`}
                        onClick={() => setModalPhoto(photo)}
                        style={{
                          width: 60,
                          height: 60,
                          objectFit: 'cover',
                          borderRadius: 6,
                          border: modalPhoto === photo ? '3px solid #388e3c' : '1px solid #ddd',
                          cursor: 'pointer',
                          flexShrink: 0,
                          transition: 'border 0.2s'
                        }}
                        onError={(e) => { e.target.onerror = null; e.target.src = placeholderImg; }}
                      />
                    ))}
                  </div>
                )}
              </div>

              {/* Bottom section: Details */}
              <div style={{ flex: '0 0 auto', width: '100%' }}>
                <h2 style={{ margin: '0 0 4px 0', fontSize: 18, color: '#1976d2' }}>{modalItem.institutename}</h2>
                {modalItem.typecategory && (
                  <div style={{ fontSize: 14, color: '#888', margin: '0 0 6px 0', fontWeight: 700 }}>{modalItem.typecategory}</div>
                )}
                <div style={{ fontSize: 14, color: '#888', marginBottom: 10, fontWeight: 600 }}>
                  📍 {modalItem.locationname} • {modalItem.areaname}
                </div>

                <div style={{ marginTop: 4, marginBottom: 8 }}>
                  <strong style={{ fontSize: '0.90rem', color: '#333', display: 'inline' }}>Description:</strong>
                  <span style={{ marginLeft: 6, color: '#333', lineHeight: 1.4, fontSize: '0.90rem', display: 'inline' }}>{modalItem.aboutdescription}</span>
                </div>

                {/* Contact Details */}
                {(modalItem.contactperson || modalItem.contactnumber || modalItem.emailid || modalItem.address1) && (
                  <div style={{ marginTop: 8 }}>
                    <strong style={{ fontSize: 15, color: '#333' }}>Contact Details:</strong>
                    {modalItem.contactperson && (
                      <div style={{ fontSize: 13, fontWeight: 800, color: '#666', marginTop: 4 }}>
                        Name: <span style={{ fontWeight: 600, color: '#555' }}>{modalItem.contactperson}</span>
                      </div>
                    )}
                    {modalItem.contactnumber && (
                      <a 
                        href={`tel:${(modalItem.contactnumber || '').replace(/\s/g, '')}`}
                        style={{ 
                          fontSize: 13, 
                          color: '#d32f2f', 
                          marginTop: 4, 
                          fontWeight: 600,
                          textDecoration: 'none',
                          display: 'block',
                          cursor: 'pointer'
                        }}
                      >
                        📞 {modalItem.contactnumber}
                      </a>
                    )}
                    {modalItem.emailid && (
                      <div style={{ marginTop: 4 }}>
                        <strong style={{ fontSize: 13, color: '#333' }}>Email:</strong>
                        <a href={`mailto:${modalItem.emailid}`} style={{ fontSize: 13, color: '#1976d2', textDecoration: 'none', marginLeft: 8 }}>{modalItem.emailid}</a>
                      </div>
                    )}
                    {modalItem.website && (
                      <div style={{ marginTop: 4 }}>
                        <strong style={{ fontSize: 13, color: '#333' }}>Website:</strong>
                        <a href={modalItem.website} target="_blank" rel="noopener noreferrer" style={{ fontSize: 13, color: '#1976d2', textDecoration: 'none', marginLeft: 8 }}>{modalItem.website}</a>
                      </div>
                    )}
                    {modalItem.address1 && (
                      <div style={{ marginTop: 4 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          <strong style={{ fontSize: 13, color: '#333' }}>Address:</strong>
                          <span style={{ fontSize: 13, color: '#666' }}>{modalItem.address1}</span>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Social media links */}
                {(modalItem.sm_instagram || modalItem.sm_facebook || modalItem.sm_youtube) && (
                  <div style={{ marginTop: 10, paddingTop: 8, borderTop: '1px solid #eee' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <strong style={{ fontSize: 14, color: '#333' }}>Connect:</strong>
                      <div style={{ display: 'flex', gap: 12 }}>
                        {modalItem.sm_instagram && (
                          <a
                            href={`https://instagram.com/${modalItem.sm_instagram}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ 
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  width: 28,
                                  height: 28,
                                  borderRadius: 6,
                                  background: 'linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)',
                                  color: '#fff',
                                  fontSize: 14,
                                  fontWeight: 'bold',
                                  textDecoration: 'none',
                                  fontFamily: 'Arial, sans-serif',
                                  transition: 'transform 0.2s'
                                }}
                            onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                            onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                            title="Instagram"
                          >
                            IG
                          </a>
                        )}
                        {modalItem.sm_facebook && (
                          <a
                            href={`https://facebook.com/${modalItem.sm_facebook}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ 
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  width: 28,
                                  height: 28,
                                  borderRadius: 6,
                                  background: '#1877F2',
                                  color: '#fff',
                                  fontSize: 14,
                                  fontWeight: 'bold',
                                  textDecoration: 'none',
                                  fontFamily: 'Arial, sans-serif',
                                  transition: 'transform 0.2s'
                                }}
                            onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                            onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                            title="Facebook"
                          >
                            f
                          </a>
                        )}
                        {modalItem.sm_youtube && (
                          <a
                            href={`https://youtube.com/${modalItem.sm_youtube}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ 
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  width: 28,
                                  height: 28,
                                  borderRadius: 6,
                                  background: '#FF0000',
                                  color: '#fff',
                                  fontSize: 14,
                                  fontWeight: 'bold',
                                  textDecoration: 'none',
                                  fontFamily: 'Arial, sans-serif',
                                  transition: 'transform 0.2s'
                                }}
                            onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                            onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                            title="YouTube"
                          >
                            ▶
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Call and Share buttons */}
                <div style={{ position: 'relative', marginTop: 16, display: 'flex', gap: 10, alignItems: 'center' }}>
                  {/* Call button, only if contactnumber exists */}
                  {modalItem.contactnumber && (
                    <button
                      onClick={() => window.location.href = `tel:${modalItem.contactnumber.replace(/\s/g, '')}`}
                      style={{
                        padding: '6px 10px',
                        background: '#4CAF50',
                        color: '#fff',
                        border: 'none',
                        borderRadius: 5,
                        fontSize: 15,
                        fontWeight: 600,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4,
                        transition: 'background 0.2s'
                      }}
                      title="Call"
                      onMouseEnter={e => e.currentTarget.style.background = '#357a38'}
                      onMouseLeave={e => e.currentTarget.style.background = '#4CAF50'}
                    >
                      <span role="img" aria-label="Call">📞</span> Call
                    </button>
                  )}
                  <button
                    onClick={() => setShowShareMenu(!showShareMenu)}
                    style={{
                      padding: '6px 10px',
                      background: '#1976d2',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 5,
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4,
                      transition: 'background 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#1565c0'}
                    onMouseLeave={(e) => e.currentTarget.style.background = '#1976d2'}
                  >
                    📤 Share
                  </button>
                  {/* Share dropdown menu */}
                  {showShareMenu && (
                    <div style={{
                      position: 'absolute',
                      bottom: '100%',
                      left: 0,
                      right: 0,
                      marginBottom: 8,
                      background: '#fff',
                      border: '1px solid #ddd',
                      borderRadius: 8,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      zIndex: 100,
                      overflow: 'hidden'
                    }}>
                      {/* WhatsApp option */}
                      <button
                        onClick={() => {
                          const shareText = `*${modalItem.institutename}*\n${modalItem.typecategory || ''}\n📍 ${modalItem.locationname || ''}\n📞 ${modalItem.contactnumber || ''}${modalItem.emailid ? `\n✉️ ${modalItem.emailid}` : ''}`;
                          const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(shareText)}`;
                          window.open(whatsappUrl, '_blank');
                          setShowShareMenu(false);
                        }}
                        style={{
                          width: '100%',
                          padding: '10px 16px',
                          background: '#25D366',
                          color: '#fff',
                          border: 'none',
                          borderBottom: '1px solid #eee',
                          fontSize: 24,
                          fontWeight: 500,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          textAlign: 'center',
                          transition: 'background 0.2s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = '#20ba5a'}
                        onMouseLeave={(e) => e.currentTarget.style.background = '#25D366'}
                      >
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg">
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                        </svg>
                      </button>
                      
                      {/* Email option */}
                      <button
                        onClick={() => {
                          const subject = encodeURIComponent(`Check out ${modalItem.institutename}`);
                          const body = encodeURIComponent(
                            `${modalItem.institutename}\n${modalItem.typecategory || ''}\n\nLocation: ${modalItem.locationname || ''}\n${modalItem.address1 ? `Address: ${modalItem.address1}\n` : ''}Contact: ${modalItem.contactnumber || ''}\n${modalItem.emailid ? `Email: ${modalItem.emailid}\n` : ''}\n${modalItem.aboutdescription ? `\nAbout:\n${modalItem.aboutdescription}` : ''}`
                          );
                          window.location.href = `mailto:?subject=${subject}&body=${body}`;
                          setShowShareMenu(false);
                        }}
                        style={{
                          width: '100%',
                          padding: '10px 16px',
                          background: '#fff',
                          color: '#333',
                          border: 'none',
                          fontSize: 24,
                          fontWeight: 500,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          textAlign: 'center',
                          transition: 'background 0.2s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = '#f5f5f5'}
                        onMouseLeave={(e) => e.currentTarget.style.background = '#fff'}
                      >
                        ✉️
                      </button>

                      {/* Copy Link option */}
                      <button
                        onClick={async () => {
                          // Wait for copy to complete (or fallback) before closing menu
                          await copyToClipboard(window.location.href);
                          setShowShareMenu(false);
                        }}
                        style={{
                          width: '100%',
                          padding: '10px 16px',
                          background: '#fff',
                          color: '#333',
                          border: 'none',
                          fontSize: 24,
                          fontWeight: 500,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          textAlign: 'center',
                          transition: 'background 0.2s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = '#f5f5f5'}
                        onMouseLeave={(e) => e.currentTarget.style.background = '#fff'}
                      >
                        🔗
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Toast notification */}
      {showToast && (
        <div style={{
          position: 'fixed',
          bottom: 20,
          right: 20,
          background: '#388e3c',
          color: '#fff',
          padding: '12px 20px',
          borderRadius: 6,
          fontSize: 14,
          zIndex: 10000,
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
        }}>
          Link copied to clipboard!
        </div>
      )}
    </div>
  );
};

export default Educational;
